import AutoCompleteChip from '@components/standardAutoCompleteSearch/AutoCompleteChip'
import StandardAutoCompleteSearch, {
  AutoCompleteOption,
  AutoCompleteRenderChipProps,
  AutoCompleteResultsZIndexPlacement,
} from '@components/standardAutoCompleteSearch/StandardAutoCompleteSearch'

import { colors } from '@globals/themes/defaultTheme'
import {
  FederatedSearchRecipientsParams,
  FederatedSearchRecipientsResults,
  TeamDisplayData,
  TenantMemberDisplayData,
  TenantMemberOrTeamDisplayData,
} from '@kudos/http-client'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import UserNameDisplay from '@shared/userDisplay/UserNameDisplay'
import { t } from '@transifex/native'
import { T } from '@transifex/react'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import {
  Control,
  Controller,
  FieldName,
  FieldPathValue,
  FieldValues,
  Path,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import ErrorText from '../ErrorText'
import { generateTeamName, getTeamIcon } from '../teamsAutoComplete/utils'
import { getRecipientAvatar, getRecipientChipLabel } from './recipientDisplayUtils'
import useSearchTenantMembersAndTeams from './useSearchTenantMembersAndTeams'

const renderChip = (
  option: AutoCompleteOption<TenantMemberOrTeamDisplayData>,
  props: AutoCompleteRenderChipProps,
  showAvatars?: boolean,
) => {
  const { key, ...otherProps } = props
  const isTeam = 'teamType' in option.value
  if (isTeam) {
    const team = option.value as TeamDisplayData
    return (
      <AutoCompleteChip
        key={key}
        {...otherProps}
        label={getRecipientChipLabel(option.value)}
        icon={!showAvatars ? getTeamIcon(team.teamType) : undefined}
        avatar={showAvatars ? getRecipientAvatar(option.value) : undefined}
      />
    )
  } else {
    return (
      <AutoCompleteChip
        key={key}
        {...otherProps}
        translate="no"
        label={getRecipientChipLabel(option.value)}
        avatar={showAvatars ? getRecipientAvatar(option.value) : undefined}
      />
    )
  }
}

export const renderOption = (
  props: object,
  option: AutoCompleteOption<TenantMemberOrTeamDisplayData>,
) => {
  const isTeam = 'teamType' in option.value

  return (
    <Stack
      {...props}
      sx={{
        '& svg': {
          color: colors.grayDark,
        },
      }}
      key={option.id}
      direction="row"
      useFlexGap
      gap={1}>
      {isTeam ? getTeamIcon((option.value as TeamDisplayData).teamType) : <PersonOutlineIcon />}
      {isTeam ? (
        <Typography>
          {generateTeamName(
            (option.value as TeamDisplayData).name,
            (option.value as TeamDisplayData).segmentType,
          )}
        </Typography>
      ) : (
        <Typography translate="no">
          <UserNameDisplay user={option.value as TenantMemberDisplayData} />
        </Typography>
      )}
      {isTeam && (option.value as TeamDisplayData).activeMemberCount && (
        <Typography variant="caption">
          <T
            _str="{activeMemberCountNum, plural, one {{activeMemberCount} user} other {{activeMemberCount} users}}"
            activeMemberCountNum={(option.value as TeamDisplayData).activeMemberCount}
            activeMemberCount={(
              (option.value as TeamDisplayData).activeMemberCount ?? 0
            ).toLocaleString()}
          />
        </Typography>
      )}
    </Stack>
  )
}

export type TenantMembersAndTeamsSearchFieldProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  label?: ReactNode
  /** Federated team + member search; results omit already-selected recipient IDs. */
  searchFn: (params: FederatedSearchRecipientsParams) => Promise<FederatedSearchRecipientsResults>
  /** Optional delegator for recognition flows that search on someone else's behalf. */
  delegatorId?: string
  showAvatars?: boolean
  disabled?: boolean
  zIndexPlacement?: AutoCompleteResultsZIndexPlacement
}

/**
 * Multi-select autocomplete for teams and tenant members, wired to react-hook-form.
 *
 * Form value is `TenantMemberOrTeamDisplayData[]`. Used for recognition recipients
 * and similar flows. {@link useSearchTenantMembersAndTeams} keeps selected IDs out
 * of the dropdown; selection writes directly from autocomplete option values.
 */
const TenantMembersAndTeamsSearchField = <T extends FieldValues>({
  name,
  control,
  label,
  searchFn,
  delegatorId,
  showAvatars,
  disabled,
  zIndexPlacement,
}: TenantMembersAndTeamsSearchFieldProps<T>) => {
  const [searchText, setSearchText] = useState<string>('')
  const { setValue } = useFormContext()
  const [options, setOptions] = useState<AutoCompleteOption<TenantMemberOrTeamDisplayData>[]>([])

  const selectedMembers = useWatch({ control, name })
  const selectedMembersIds = useMemo(
    () => selectedMembers?.map(({ id }: { id: string }) => id),
    [selectedMembers],
  )

  const { searchedTenantMembersAndTeams, isFetching } = useSearchTenantMembersAndTeams(
    searchText,
    selectedMembersIds,
    searchFn,
    delegatorId ?? undefined,
  )

  const onSelectedOptionsChanged = (
    options: AutoCompleteOption<TenantMemberOrTeamDisplayData>[],
  ) => {
    const selectedOptions: FieldPathValue<FieldValues, FieldName<FieldValues>> = options.map(
      option => option.value,
    )
    setValue(name, selectedOptions, { shouldValidate: true, shouldDirty: true })
  }

  useEffect(() => {
    setOptions(
      searchedTenantMembersAndTeams?.map(
        (member: TenantMemberOrTeamDisplayData) =>
          ({
            avatar: showAvatars ? getRecipientAvatar(member) : undefined,
            icon: 'teamType' in member && !showAvatars ? getTeamIcon(member.teamType) : undefined,
            value: member,
            id: member.id,
          }) as AutoCompleteOption<TenantMemberOrTeamDisplayData>,
      ) || [],
    )
  }, [searchedTenantMembersAndTeams, showAvatars])

  return (
    <Stack>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => (
          <>
            <StandardAutoCompleteSearch<TenantMemberOrTeamDisplayData>
              searchText={searchText}
              value={selectedMembers?.map((member: TenantMemberOrTeamDisplayData) => ({
                value: member,
                id: member.id,
              }))}
              options={options}
              onSearchTextChanged={setSearchText}
              onSelectedOptionsChanged={onSelectedOptionsChanged}
              placeholder={t('Search users or teams...')}
              renderChip={(option, props) => renderChip(option, props, showAvatars)}
              label={label}
              renderOption={renderOption}
              groupBy={option => ('teamType' in option.value ? t('Teams') : t('Users'))}
              disabled={disabled}
              zIndexPlacement={zIndexPlacement}
              loading={isFetching}
              hideDropdown={true}
            />
            {!!error && <ErrorText>{error.message}</ErrorText>}
          </>
        )}
      />
    </Stack>
  )
}

export default TenantMembersAndTeamsSearchField
